import React from 'react';
import { TFunction } from 'i18next';
import {
  DarwinCheckbox,
  DarwinInput,
  DarwinSelect,
  DarwinTreeSelect,
  DTableColumnsType,
} from '@darwin-ui';
import {
  ProcessFormAcceptedCommodity,
  ProcessFormSelectOption,
  ProcessFormAddressAcceptedValues,
  DataFieldCols,
  ProcessFormAddressAcceptedValuesWithDescription,
  ProcessFieldTypes,
} from 'types/dataCaptureTypes';
import { TreeNode } from '../../ConfigurationForm/configs/types';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import styles from './index.module.css';
import { translateField, translateFormSelect } from 'utils/common';
import { translateProductLabel } from 'utils/product';
import { useTranslation } from 'react-i18next';

const { Option } = DarwinSelect;
const DEFAULT_INPUT_WIDTH = 300;
const DEFAULT_INPUT_MAX_HEIGHT = 150;

// Constants for field names to avoid magic strings
export const PRODUCT_FIELD = 'Product';
export const PROCESS_DESCRIPTION_FIELD = 'Location Description (Recipient)';
export const COOLING_RECIPIENT_LOCATION_DESCRIPTION =
  'Recipient Location Description';
export const HARVEST_LOCATION = 'Harvest Location';
export const COOLING_LOCATION = 'Cooling Location';
export const PACKING_LOCATION = 'Packing Location';
export const SHIPPING_LOCATION = 'Shipped Location';
export const RECEIVING_LOCATION = 'Receiving Location';
export const FIRST_LAND_BASED_RECEIVER_LOCATION =
  'Land-Based Receiving Location';
export const TRANSFORMATION_LOCATION = 'Process Location';
export const TRANSFORMATION_RECIPIENT_LOCATION = 'Recipient Location';

export const LOCATION_FIELD_LIST = [
  HARVEST_LOCATION,
  COOLING_LOCATION,
  PACKING_LOCATION,
  SHIPPING_LOCATION,
  RECEIVING_LOCATION,
  FIRST_LAND_BASED_RECEIVER_LOCATION,
  TRANSFORMATION_LOCATION,
];

/** Converts accepted product categories into a tree structure */
export const parseProductTreeData = (
  acceptedValues: ProcessFormAcceptedCommodity[],
  t: TFunction
): TreeNode[] =>
  acceptedValues.map(commodity => ({
    title: translateProductLabel(
      t,
      'commodity',
      commodity.translationKey,
      commodity.commodityName
    ),
    value: commodity.commodityId,
    children: commodity.varieties.map(variety => ({
      title: translateProductLabel(
        t,
        'variety',
        variety.translationKey,
        variety.varietyName
      ),
      value: variety.varietyId,
      children: variety.products.map(product => ({
        title: translateProductLabel(
          t,
          'product',
          product.translationKey,
          product.productName
        ),
        value: product.productName,
        children: product.finalProducts.map(finalProduct => ({
          title: finalProduct.finalProductName,
          value: finalProduct.finalProductId,
        })),
      })),
    })),
  }));

/**
 * Unified location tree parser that handles both simple locations and process-based locations
 * For simple addresses: shows address -> locations
 * For addresses with processes: shows address -> locations (flattened from all processes)
 */
export const parseLocationTreeData = (
  data:
    | ProcessFormAddressAcceptedValues
    | ProcessFormAddressAcceptedValuesWithDescription
): TreeNode[] => {
  return (
    data.addresses?.map(address => {
      let children: TreeNode[] = [];

      // Check if this is the enhanced structure with processes
      if ('processes' in address && address.processes) {
        // Flatten all locations from all processes under each address
        children = address.processes.flatMap(
          process =>
            process.locations?.map(loc => ({
              title: loc.name,
              value: `${loc.id}`,
            })) ?? []
        );
      } else if ('locations' in address && address.locations) {
        // Simple location structure
        children = address.locations.map(loc => ({
          title: loc.name,
          value: loc.id,
        }));
      }

      return {
        title: `${address.name} - ${address.fullAddress}`,
        value: address.id,
        children,
      };
    }) ?? []
  );
};
/** Recursively collects all leaf nodes */
const getAllLeaves = (nodes: TreeNode[]): TreeNode[] =>
  nodes.reduce((leaves: TreeNode[], node: TreeNode) => {
    return node.children && node.children.length > 0
      ? [...leaves, ...getAllLeaves(node.children)]
      : [...leaves, node];
  }, []);

/** Retrieves all leaf nodes for a selected value */
export const getLeafNodesForValue = (
  nodes: TreeNode[],
  selectedValue: string
): TreeNode[] => {
  for (const node of nodes) {
    if (node.value === selectedValue) {
      return node.children && node.children.length > 0
        ? getAllLeaves(node.children)
        : [node];
    } else if (node.children) {
      const result = getLeafNodesForValue(node.children, selectedValue);
      if (result.length > 0) return result;
    }
  }
  return [];
};

/** Component to render the accepted values input */
interface RenderAcceptedValuesProps {
  record: DataFieldCols;
  onFieldChange: <T extends keyof DataFieldCols>(
    key: string,
    field: T,
    newValue: DataFieldCols[T]
  ) => void;
}

const RenderAcceptedValues: React.FC<RenderAcceptedValuesProps> = ({
  record,
  onFieldChange,
}) => {
  const { t } = useTranslation();

  const { acceptedValues, accepted_values, field_type, name } = record;
  const handleAcceptedValuesChange = (newVal: string[]) => {
    onFieldChange(record.key, 'acceptedValues', newVal);
    if (
      !newVal.length ||
      (record.defaultValue && !newVal.includes(record.defaultValue))
    ) {
      onFieldChange(record.key, 'defaultValue', undefined);
    }
  };

  if (name === PRODUCT_FIELD) {
    const treeData = parseProductTreeData(
      accepted_values.value as ProcessFormAcceptedCommodity[],
      t
    );
    return (
      <DarwinTreeSelect
        placeholder={
          accepted_values.read_only ? '–' : t('process-flow.configure.select')
        }
        treeData={treeData}
        treeCheckable
        treeNodeFilterProp="title"
        style={{
          width: DEFAULT_INPUT_WIDTH,
          maxHeight: DEFAULT_INPUT_MAX_HEIGHT,
        }}
        value={acceptedValues}
        className={`${styles.productSelector} ${acceptedValues?.length ? styles.expanded : ''
          }`}
        disabled={accepted_values.read_only}
        onChange={handleAcceptedValuesChange}
      />
    );
  }

  // Unified handling for all location-based fields
  if (
    name === PROCESS_DESCRIPTION_FIELD ||
    name === COOLING_RECIPIENT_LOCATION_DESCRIPTION ||
    name === TRANSFORMATION_RECIPIENT_LOCATION ||
    LOCATION_FIELD_LIST.includes(name)
  ) {
    const treeData = parseLocationTreeData(
      accepted_values.value as
      | ProcessFormAddressAcceptedValues
      | ProcessFormAddressAcceptedValuesWithDescription
    );
    return (
      <DarwinTreeSelect
        placeholder={
          accepted_values.read_only ? '–' : t('process-flow.configure.select')
        }
        treeData={treeData}
        treeCheckable
        treeNodeFilterProp="title"
        style={{ width: DEFAULT_INPUT_WIDTH }}
        value={acceptedValues}
        disabled={accepted_values.read_only}
        onChange={handleAcceptedValuesChange}
      />
    );
  }

  if (field_type !== ProcessFieldTypes.Select) {
    return (
      <DarwinSelect
        disabled
        placeholder="–"
        style={{ width: DEFAULT_INPUT_WIDTH }}
      />
    );
  }
  const options =
    (Array.isArray(accepted_values.value) &&
      (accepted_values.value as ProcessFormSelectOption[]).map(opt => ({
        value: opt.id,
        label: opt.translationKey
          ? t(opt.translationKey)
          : translateFormSelect(t, opt.name),
      }))) ||
    [];
  return (
    <DarwinSelect
      placeholder={
        accepted_values.read_only ? '–' : t('process-flow.configure.select')
      }
      mode="multiple"
      style={{ width: DEFAULT_INPUT_WIDTH }}
      value={acceptedValues}
      disabled={accepted_values.read_only}
      onChange={handleAcceptedValuesChange}
    >
      {options?.map(opt => (
        <Option key={opt.value} value={opt.value}>
          <DarwinCheckbox
            style={{ marginRight: 5 }}
            checked={acceptedValues.includes(opt.value)}
          />
          {opt.label}
        </Option>
      ))}
    </DarwinSelect>
  );
};

/** Component to render the default value input */
interface RenderDefaultValueProps {
  record: DataFieldCols;
  onFieldChange: <T extends keyof DataFieldCols>(
    key: string,
    field: T,
    newValue: DataFieldCols[T]
  ) => void;
}

const RenderDefaultValue: React.FC<RenderDefaultValueProps> = ({
  record,
  onFieldChange,
}) => {
  const { t } = useTranslation();

  const {
    defaultValue,
    accepted_values,
    name,
    field_type,
    acceptedValues,
    default_values,
  } = record;

  if (default_values.read_only) {
    let displayValue = defaultValue;
    if (field_type === ProcessFieldTypes.Select && defaultValue) {
      const found = (accepted_values.value as ProcessFormSelectOption[]).find(
        opt => opt.id === defaultValue
      );
      if (found) {
        displayValue = found.translationKey
          ? t(found.translationKey)
          : translateFormSelect(t, found.name);
      }
    }
    return (
      <DarwinInput
        disabled
        value={displayValue}
        placeholder="–"
        style={{ width: DEFAULT_INPUT_WIDTH }}
      />
    );
  }

  // Unified handling for all location-based and product fields
  if (
    name === PRODUCT_FIELD ||
    name === PROCESS_DESCRIPTION_FIELD ||
    name === COOLING_RECIPIENT_LOCATION_DESCRIPTION ||
    name === TRANSFORMATION_RECIPIENT_LOCATION ||
    LOCATION_FIELD_LIST.includes(name)
  ) {
    const treeData =
      name === PRODUCT_FIELD
        ? parseProductTreeData(
          accepted_values.value as ProcessFormAcceptedCommodity[],
          t
        )
        : parseLocationTreeData(
          accepted_values.value as
          | ProcessFormAddressAcceptedValues
          | ProcessFormAddressAcceptedValuesWithDescription
        );

    let leafOptions: TreeNode[] = [];
    if (acceptedValues.length) {
      acceptedValues.forEach((val: string) => {
        const leaves = getLeafNodesForValue(treeData, val);
        leafOptions = leafOptions.concat(leaves);
      });
      leafOptions = leafOptions.filter(
        (option, index, self) =>
          index === self.findIndex(o => o.value === option.value)
      );
    }

    const isDisabled = default_values.read_only || !leafOptions.length;

    return (
      <DarwinSelect
        placeholder={isDisabled! ? '–' : t('process-flow.configure.select')}
        style={{ width: DEFAULT_INPUT_WIDTH }}
        value={defaultValue}
        onChange={(newVal: string | undefined) =>
          onFieldChange(record.key, 'defaultValue', newVal)
        }
        disabled={isDisabled}
        allowClear
      >
        {leafOptions.map(opt => (
          <Option key={opt.value} value={opt.value}>
            {opt.title}
          </Option>
        ))}
      </DarwinSelect>
    );
  }

  if (field_type === ProcessFieldTypes.Select) {
    const accepted = acceptedValues.length ? acceptedValues : [];
    const options = accepted.map((val: string) => {
      const found = (accepted_values.value as ProcessFormSelectOption[]).find(
        opt => opt.id === val
      );
      return found
        ? {
          value: val,
          label: found.translationKey
            ? t(found.translationKey)
            : translateFormSelect(t, found.name),
        }
        : { value: val, label: val };
    });

    let actualValue = defaultValue;
    if (defaultValue) {
      const matchingOption = options.find(o => o.label === defaultValue);
      if (!matchingOption) {
        // Try to find by original name
        const optByName = (
          accepted_values.value as ProcessFormSelectOption[]
        ).find(opt => opt.name === defaultValue);
        if (optByName) {
          actualValue = optByName.id;
        }
      } else {
        actualValue = matchingOption.value;
      }
    }

    const isDisabled = default_values.read_only || !options.length;

    return (
      <DarwinSelect
        placeholder={isDisabled ? '–' : t('process-flow.configure.select')}
        style={{ width: DEFAULT_INPUT_WIDTH }}
        value={actualValue}
        onChange={(newVal: string | undefined) =>
          onFieldChange(record.key, 'defaultValue', newVal)
        }
        disabled={isDisabled}
        allowClear
      >
        {options.map(opt => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </DarwinSelect>
    );
  }

  return (
    <DarwinInput
      style={{ width: DEFAULT_INPUT_WIDTH }}
      value={defaultValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onFieldChange(record.key, 'defaultValue', e.target.value)
      }
      disabled={true}
    />
  );
};

/** Exports the columns configuration for the data capture table */
export const getFieldsColumns = (
  t: TFunction,
  onFieldChange: <T extends keyof DataFieldCols>(
    key: string,
    field: T,
    newValue: DataFieldCols[T]
  ) => void,
  isInitialProcess: boolean
): DTableColumnsType<DataFieldCols> => {
  return [
    {
      title: t('process-flow.data-capture.fields-list.columns.field-name'),
      dataIndex: 'name',
      key: 'name',
      render: (_, record: DataFieldCols) => {
        return translateField(
          t,
          record.is_custom ? record.name : record.field_key,
          {
            subKey: 'field_name',
            by: 'key',
            isCustom: record.is_custom,
          }
        );
      },
      width: 200,
    },
    {
      title: t('process-flow.data-capture.fields-list.columns.required'),
      dataIndex: 'required',
      key: 'required',
      align: 'center',
      render: (_, record: DataFieldCols) => (
        <DarwinCheckbox
          checked={record.required.value}
          disabled={record.required.read_only}
          onChange={(e: CheckboxChangeEvent) =>
            onFieldChange(record.key, 'required', {
              ...record.required,
              value: e.target.checked,
            })
          }
        />
      ),
      width: 100,
    },
    {
      title: t('process-flow.data-capture.fields-list.columns.visible'),
      dataIndex: 'visible',
      key: 'visible',
      align: 'center',
      render: (_, record: DataFieldCols) => (
        <DarwinCheckbox
          checked={record.visible.value}
          disabled={record.visible.read_only}
          onChange={(e: CheckboxChangeEvent) =>
            onFieldChange(record.key, 'visible', {
              ...record.visible,
              value: e.target.checked,
            })
          }
        />
      ),
      width: 100,
    },
    {
      title: t('process-flow.data-capture.fields-list.columns.flow-through'),
      dataIndex: 'flowThrough',
      key: 'flowThrough',
      align: 'center',
      render: (_, record: DataFieldCols) => (
        <DarwinCheckbox
          checked={record.flowThrough.value}
          disabled={record.flowThrough.read_only || isInitialProcess}
          onChange={(e: CheckboxChangeEvent) =>
            onFieldChange(record.key, 'flowThrough', {
              ...record.flowThrough,
              value: e.target.checked,
            })
          }
        />
      ),
      width: 130,
    },
    {
      title: t('process-flow.data-capture.fields-list.columns.accepted-values'),
      dataIndex: 'acceptedValues',
      key: 'acceptedValues',
      render: (_, record: DataFieldCols) => (
        <RenderAcceptedValues record={record} onFieldChange={onFieldChange} />
      ),
    },
    {
      title: t('process-flow.data-capture.fields-list.columns.default-value'),
      dataIndex: 'defaultValue',
      key: 'defaultValue',
      render: (_, record: DataFieldCols) => (
        <RenderDefaultValue record={record} onFieldChange={onFieldChange} />
      ),
    },
  ];
};
